import { lazy, Suspense } from 'react'
import { Toaster } from "@/components/ui/toaster"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/lib/AuthContext';
import ScrollToTop from './components/ScrollToTop';
import AnalyticsTracker from './components/AnalyticsTracker';
import { LocaleProvider } from '@/lib/locale-context';
import { useThemeBootstrap } from '@/lib/use-theme';
import Home from '@/pages/Home';

// Lazy-loaded routes — code-split so the homepage bundle stays small
// and calculator/trust-page code only loads on navigation.
const CalculatorPage = lazy(() => import('@/pages/CalculatorPage'));
const CalculatorsIndexPage = lazy(() => import('@/pages/CalculatorsIndexPage'));
const AboutPage = lazy(() => import('@/pages/AboutPage'));
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage'));
const TermsPage = lazy(() => import('@/pages/TermsPage'));
const ContactPage = lazy(() => import('@/pages/ContactPage'));
const DisclaimerPage = lazy(() => import('@/pages/DisclaimerPage'));
const MethodologyPage = lazy(() => import('@/pages/MethodologyPage'));
const PageNotFound = lazy(() => import('./lib/PageNotFound'));
const USSalaryGuide = lazy(() => import('@/pages/USSalaryGuide'));
const CompoundInterestGuide = lazy(() => import('@/pages/CompoundInterestGuide'));
const CompoundInterestFormulaGuide = lazy(() => import('@/pages/CompoundInterestFormulaGuide'));
const CompoundInterestTableGuide = lazy(() => import('@/pages/CompoundInterestTableGuide'));
const Investing500MonthGuide = lazy(() => import('@/pages/Investing500MonthGuide'));

const AuthenticatedApp = () => {
  useThemeBootstrap();

  return (
    <Suspense fallback={<div className="min-h-dvh bg-background" />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/calculators" element={<CalculatorsIndexPage />} />
        <Route path="/us/calculators" element={<Navigate to="/calculators" replace />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/disclaimer" element={<DisclaimerPage />} />
        <Route path="/methodology" element={<MethodologyPage />} />
        <Route path="/guides/us-salary-2026" element={<USSalaryGuide />} />
        <Route path="/guides/compound-interest" element={<CompoundInterestGuide />} />
        <Route path="/guides/compound-interest-formula" element={<CompoundInterestFormulaGuide />} />
        <Route path="/guides/compound-interest-table" element={<CompoundInterestTableGuide />} />
        <Route path="/guides/investing-500-a-month" element={<Investing500MonthGuide />} />
        <Route path="/calculators/:slug" element={<CalculatorPage />} />
        <Route path="/:country/calculators/:slug" element={<CalculatorPage />} />
        <Route path="/:country/:region/calculators/:slug" element={<CalculatorPage />} />
        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </Suspense>
  );
};


function App() {

  return (
    <AuthProvider>
      <LocaleProvider>
        <QueryClientProvider client={queryClientInstance}>
          <Router>
            <ScrollToTop />
            <AuthenticatedApp />
            <AnalyticsTracker />
          </Router>
          <Toaster />
        </QueryClientProvider>
      </LocaleProvider>
    </AuthProvider>
  )
}

export default App